import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Brain, 
  TrendingUp, 
  AlertCircle, 
  CheckCircle, 
  Settings, 
  Download,
  Upload,
  Activity,
  Zap
} from 'lucide-react';

export const ModelManagementDashboard = () => {
  const [models] = useState([
    {
      id: '1',
      name: 'GPT-4 Turbo',
      type: 'Text Extraction',
      status: 'active',
      accuracy: 94.2,
      usage: 78,
      lastUpdated: '2 hours ago',
      version: '1.2.0'
    },
    {
      id: '2',
      name: 'Claude-3 Haiku',
      type: 'Document Classification',
      status: 'training',
      accuracy: 91.8,
      usage: 45,
      lastUpdated: '1 day ago',
      version: '1.1.3'
    },
    {
      id: '3',
      name: 'Custom OCR Model',
      type: 'OCR Processing',
      status: 'inactive',
      accuracy: 87.5,
      usage: 0,
      lastUpdated: '1 week ago',
      version: '2.0.1'
    }
  ]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'training': return 'secondary';
      case 'inactive': return 'outline';
      default: return 'outline';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return CheckCircle;
      case 'training': return Activity;
      case 'inactive': return AlertCircle;
      default: return AlertCircle;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">AI Model Management</h2>
          <p className="text-muted-foreground">Monitor and manage your AI models for bill processing</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline">
            <Upload className="h-4 w-4 mr-2" />
            Import Model
          </Button>
          <Button>
            <Settings className="h-4 w-4 mr-2" />
            Configure
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active Models</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">3</div>
            <p className="text-xs text-muted-foreground">+1 from last month</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Average Accuracy</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">91.2%</div>
            <p className="text-xs text-muted-foreground">+2.1% from last week</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Predictions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">12.4K</div>
            <p className="text-xs text-muted-foreground">This month</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Cost Efficiency</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">$0.03</div>
            <p className="text-xs text-muted-foreground">Per prediction</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="models" className="w-full">
        <TabsList>
          <TabsTrigger value="models">Models</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="training">Training</TabsTrigger>
          <TabsTrigger value="deployment">Deployment</TabsTrigger>
        </TabsList>

        <TabsContent value="models" className="space-y-4">
          <div className="grid gap-4">
            {models.map((model) => {
              const StatusIcon = getStatusIcon(model.status);
              return (
                <Card key={model.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Brain className="h-8 w-8 text-primary" />
                        <div>
                          <CardTitle className="text-lg">{model.name}</CardTitle>
                          <CardDescription>{model.type} • Version {model.version}</CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant={getStatusColor(model.status)} className="flex items-center space-x-1">
                          <StatusIcon className="h-3 w-3" />
                          <span>{model.status}</span>
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">Accuracy</span>
                          <span className="text-sm text-muted-foreground">{model.accuracy}%</span>
                        </div>
                        <Progress value={model.accuracy} className="h-2" />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">Usage</span>
                          <span className="text-sm text-muted-foreground">{model.usage}%</span>
                        </div>
                        <Progress value={model.usage} className="h-2" />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Last Updated: {model.lastUpdated}</span>
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm">
                            <Settings className="h-3 w-3" />
                          </Button>
                          <Button variant="outline" size="sm">
                            <Download className="h-3 w-3" />
                          </Button>
                          <Button size="sm">
                            <Zap className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="performance" className="space-y-4">
          <Alert>
            <TrendingUp className="h-4 w-4" />
            <AlertDescription>
              Model performance has improved by 15% over the last month with optimized training data.
            </AlertDescription>
          </Alert>
          
          <Card>
            <CardHeader>
              <CardTitle>Performance Metrics</CardTitle>
              <CardDescription>Real-time model performance tracking</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <span className="text-sm font-medium">Precision</span>
                    <Progress value={93.4} className="h-2" />
                    <span className="text-xs text-muted-foreground">93.4%</span>
                  </div>
                  <div className="space-y-2">
                    <span className="text-sm font-medium">Recall</span>
                    <Progress value={89.7} className="h-2" />
                    <span className="text-xs text-muted-foreground">89.7%</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <span className="text-sm font-medium">F1 Score</span>
                    <Progress value={91.5} className="h-2" />
                    <span className="text-xs text-muted-foreground">91.5%</span>
                  </div>
                  <div className="space-y-2">
                    <span className="text-sm font-medium">Response Time</span>
                    <Progress value={75} className="h-2" />
                    <span className="text-xs text-muted-foreground">1.2s avg</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="training" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Training Pipeline</CardTitle>
              <CardDescription>Manage model training and retraining processes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Scheduled Retraining</p>
                    <p className="text-sm text-muted-foreground">Next training session in 3 days</p>
                  </div>
                  <Button variant="outline">Configure</Button>
                </div>
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Training Data Quality</p>
                    <p className="text-sm text-muted-foreground">12,450 validated samples</p>
                  </div>
                  <Badge variant="secondary">Excellent</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deployment" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Deployment Status</CardTitle>
              <CardDescription>Monitor model deployment and rollout</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Production Environment</span>
                  <Badge variant="default">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Healthy
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Staging Environment</span>
                  <Badge variant="secondary">
                    <Activity className="h-3 w-3 mr-1" />
                    Testing
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Auto-scaling</span>
                  <Badge variant="outline">Enabled</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};